// @flow

import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/profile/actionCreator";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import {
  GetServiceDeskIssueTypes,
  GetServiceDeskIssueFormData,
  AddServiceDeskIssue,
  AddServiceDeskIssueAttachment,
  GetServiceDeskTickets,
  GetServiceDeskTicketContent,
} from "@modules/profile/constants/actions";
import type { IssueType, IssueFormData } from "@modules/profile/reducer/service-desk/types";
import { dataURLtoFile } from "@utils/file";
import config from "@config";

function* onGetServiceDeskIssueTypes(): Generator<*, void, { data: Array<IssueType> }> {
  try {
    const response = yield call(
      http.get,
      `${config.serviceDeskEndpointBaseUrl}api/forms/available`,
    );

    yield put(actionCreators.GetServiceDeskIssueTypesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetServiceDeskIssueTypesError(e.response));
  }
}

type ServiceDeskTicketsRequest = $Exact<{
  page: number,
}>;

function* onGetServiceDeskTickets({
  payload,
}: Action<ServiceDeskTicketsRequest>): Generator<*, void, *> {
  try {
    const response = yield call(
      http.get,
      `${config.serviceDeskEndpointBaseUrl}api/tickets/all/per-employee`,
      {
        params: {
          page: payload?.page || 1,
          size: config.resultsPerPage,
          sort: "createdAt,desc",
        },
      },
    );

    yield put(actionCreators.GetServiceDeskTicketsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetServiceDeskTicketsError(e.response));
  }
}

type IssueFormDataRequest = $Exact<{
  issueId: number,
}>;

function* onGetServiceDeskIssueFormData({
  payload,
}: Action<IssueFormDataRequest>): Generator<*, void, { data: IssueFormData }> {
  try {
    if (!payload || !payload.issueId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.get,
      `${config.serviceDeskEndpointBaseUrl}api/forms/${payload.issueId}/contents`,
    );

    yield put(actionCreators.GetServiceDeskIssueFormDataSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetServiceDeskIssueTypesError(e.response));
  }
}

type IssueFormPostRequest = $Exact<{
  data: Object,
  issueId: number,
}>;

function* onAddServiceDeskIssue({ payload }: Action<IssueFormPostRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const data = { ...payload.data };
    const files = data.fields.attachment;
    if (files.length) {
      delete data.fields.attachment; // we need to add attachments only after ticket got created. That's why it's 2 different api calls
    }

    const response = yield call(
      http.post,
      `${config.serviceDeskEndpointBaseUrl}api/forms/${payload.issueId}/create`,
      payload.data,
    );

    yield put(actionCreators.AddServiceDeskIssueSuccess(response.data));
    yield put(actionCreators.GetServiceDeskTickets());
    yield put(sendSuccessMessage("TICKET_CREATED"));
    if (files.length) {
      yield put(actionCreators.AddServiceDeskIssueAttachment({ files, id: response.data.id }));
    }
  } catch (e) {
    yield put(actionCreators.AddServiceDeskIssueError(e.response));
  }
}

type IssueFormPostAttachmentRequest = $Exact<{
  files: string[],
  id: number,
}>;

export function* onAddServiceDeskIssueAttachment({
  payload,
}: Action<IssueFormPostAttachmentRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.files || !payload.id) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const data = new FormData();
    payload.files.forEach(el => {
      const file = dataURLtoFile(el);
      data.append("files", file.blob, file.name);
    });

    yield call(
      http.post,
      `${config.serviceDeskEndpointBaseUrl}api/tickets/${payload.id}/attachments`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield put(actionCreators.AddServiceDeskIssueAttachmentSuccess());
  } catch (e) {
    yield put(actionCreators.AddServiceDeskIssueAttachmentError(e.response));
  }
}

type TicketContentPayload = $Exact<{
  ticketId: number,
}>;

export function* onGetServiceDeskTicketContent({
  payload,
}: Action<TicketContentPayload>): Generator<*, void, *> {
  try {
    if (!payload || !payload.ticketId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.serviceDeskEndpointBaseUrl}api/tickets/${payload.ticketId}/contents`,
    );

    yield put(
      actionCreators.GetServiceDeskTicketContentSuccess(response.data.ticketContents.fields),
    );
    yield put(
      actionCreators.GetServiceDeskIssueFormData({ issueId: response.data.jiraIssueTypeId }),
    );
  } catch (e) {
    yield put(actionCreators.GetServiceDeskTicketContentError(e.response));
  }
}

export default function* ServiceDeskSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetServiceDeskIssueTypes, onGetServiceDeskIssueTypes),
    takeEvery(GetServiceDeskIssueFormData, onGetServiceDeskIssueFormData),
    takeEvery(AddServiceDeskIssue, onAddServiceDeskIssue),
    takeEvery(AddServiceDeskIssueAttachment, onAddServiceDeskIssueAttachment),
    takeEvery(GetServiceDeskTickets, onGetServiceDeskTickets),
    takeEvery(GetServiceDeskTicketContent, onGetServiceDeskTicketContent),
  ]);
}
